import React, { createContext, useContext, useEffect, useState } from 'react';
import { utils } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;
    let bootstrapped = false;
    let fallbackTimer = null;
    let resolveInitialAuthState;
    const initialAuthState = new Promise(resolve => {
      resolveInitialAuthState = resolve;
    });

    const onAuthUser = (nextUser) => {
      resolveInitialAuthState();
      setUser(nextUser);
      setLoading(false);

      if (typeof window === 'undefined') return;

      window.__authContext = {
        user: nextUser,
        isAuthenticated: !!nextUser,
        userId: null,
        registrationPromise: null
      };

      if (!nextUser) {
        window.dispatchEvent(new Event('fileshare-user-id-changed'));
        return;
      }

      // Registration links this browser's anonymous files to the account's
      // canonical ID. Keep that returned ID for all future upload/list calls.
      const registrationPromise = (async () => {
        const currentUserId = utils.getCurrentUserId();
        const response = await fetch('/api/user/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: nextUser.email,
            firebaseUid: nextUser.uid,
            displayName: nextUser.displayName || null,
            anonymousUserId: currentUserId
          })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success || !data.userId) {
          throw new Error(data.error || data.message || 'Could not link this account to its files');
        }

        if (window.__authContext?.user?.uid === nextUser.uid) {
          window.__authContext = {
            ...window.__authContext,
            userId: data.userId
          };
          window.dispatchEvent(new CustomEvent('fileshare-user-id-changed', {
            detail: { userId: data.userId }
          }));
        }
        return data.userId;
      })().catch((error) => {
        console.error('Account file linking failed:', error);
        return null;
      });

      window.__authContext.registrationPromise = registrationPromise;
    };

    const detachAuthBootListeners = () => {
      window.removeEventListener('scroll', bootstrapAuth);
      window.removeEventListener('touchstart', bootstrapAuth);
      window.removeEventListener('keydown', bootstrapAuth);
      window.removeEventListener('mousedown', bootstrapAuth);
    };

    const bootstrapAuth = async () => {
      if (bootstrapped || cancelled) return;
      bootstrapped = true;
      detachAuthBootListeners();
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }

      try {
        const { onAuthStateChange } = await import('../firebase/auth');
        if (cancelled) return;
        unsubscribe = onAuthStateChange(onAuthUser);
      } catch (_) {
        if (!cancelled) {
          resolveInitialAuthState();
          setLoading(false);
        }
      }
    };

    const ensureAuthReady = async () => {
      await bootstrapAuth();
      await Promise.race([
        initialAuthState,
        new Promise(resolve => setTimeout(resolve, 8000))
      ]);
      const registrationPromise = window.__authContext?.registrationPromise;
      if (registrationPromise) await registrationPromise;
    };

    window.__ensureAuthReady = ensureAuthReady;

    const isAuthCriticalRoute =
      window.location.pathname.startsWith('/uploads') ||
      window.location.pathname.startsWith('/profile') ||
      window.location.pathname.startsWith('/pricing') ||
      window.location.pathname.startsWith('/admin');

    if (isAuthCriticalRoute) {
      bootstrapAuth();
    } else {
      // Home/marketing routes: defer Firebase auth boot to reduce startup JS blocking.
      window.addEventListener('scroll', bootstrapAuth, { passive: true });
      window.addEventListener('touchstart', bootstrapAuth, { passive: true });
      window.addEventListener('keydown', bootstrapAuth);
      window.addEventListener('mousedown', bootstrapAuth);

      fallbackTimer = setTimeout(bootstrapAuth, 12000);
      setLoading(false);
    }

    return () => {
      cancelled = true;
      if (window.__ensureAuthReady === ensureAuthReady) {
        delete window.__ensureAuthReady;
      }
      detachAuthBootListeners();
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const logout = async () => {
    try {
      const { signOutUser } = await import('../firebase/auth');
      await signOutUser();
    } catch (err) {
      console.error('Error signing out of Firebase:', err);
    }

    setUser(null);
    setLoading(false);

    if (typeof window !== 'undefined') {
      window.__authContext = { user: null, isAuthenticated: false, userId: null, registrationPromise: null };

      try {
        const newAnonId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? `user_${crypto.randomUUID()}`
          : `user_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

        localStorage.setItem('fileshare_user_id', newAnonId);
        localStorage.removeItem('fileshare_original_user_id');

        const exp = new Date();
        exp.setFullYear(exp.getFullYear() + 1);
        document.cookie = `fileshare_user_id=${encodeURIComponent(newAnonId)}; expires=${exp.toUTCString()}; path=/; SameSite=Lax`;
        if (window.location.hostname.includes('largetransfer.net')) {
          document.cookie = `fileshare_user_id=${encodeURIComponent(newAnonId)}; expires=${exp.toUTCString()}; path=/; domain=.largetransfer.net; SameSite=Lax`;
        }
      } catch (storageErr) {
        console.warn('Could not reset user ID on logout:', storageErr);
      }
      window.dispatchEvent(new Event('fileshare-user-id-changed'));
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


